import { useEffect, useRef, useState } from "react";

const steps = [
  {
    number: "01",
    title: "Market Validation",
    desc: "We analyze your product-market fit for CEE, identify the highest-value segments and map your competitive landscape — so you enter with precision, not guesswork.",
    detail: "ICP mapping · Competitive analysis · Opportunity scoring",
  },
  {
    number: "02",
    title: "Pipeline Creation",
    desc: "We build your outbound system using AI-powered sequences, local language outreach and battle-tested targeting to generate qualified meetings on your calendar.",
    detail: "AI outreach · Lead sourcing · CRM setup · Booking automation",
  },
  {
    number: "03",
    title: "Deal Closing",
    desc: "Our local experts join discovery calls, handle negotiations and guide prospects through to signature — with full visibility and reporting throughout.",
    detail: "Local negotiation · Deal advisory · Contract close",
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function Process() {
  const { ref, inView } = useInView();

  return (
    <section id="process" className="bg-[#060606] py-32 px-6 relative overflow-hidden">
      {/* Decorative line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/20 to-transparent" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={ref} className={`text-center mb-24 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-[#C9A84C]/40" />
            <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">How It Works</span>
            <div className="w-16 h-px bg-[#C9A84C]/40" />
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-white">
            Our <em className="text-[#C9A84C] not-italic">3-step</em> system
          </h2>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line - desktop */}
          <div className="hidden lg:block absolute top-16 left-[16.67%] right-[16.67%] h-px bg-[#C9A84C]/15" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#C9A84C]/8">
            {steps.map((step, i) => (
              <div
                key={step.number}
                className={`bg-[#060606] p-12 lg:p-14 group hover:bg-[#0a0a0a] transition-all duration-500 cursor-default ${
                  inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {/* Step number */}
                <div className="relative mb-8">
                  <span className="font-['Cormorant_Garamond'] text-7xl font-light text-[#C9A84C]/15 group-hover:text-[#C9A84C]/25 transition-colors duration-300 leading-none">
                    {step.number}
                  </span>
                  <div className="absolute bottom-2 left-0 w-8 h-px bg-[#C9A84C] group-hover:w-16 transition-all duration-500" />
                </div>

                {/* Content */}
                <h3 className="font-['Cormorant_Garamond'] text-2xl lg:text-3xl font-light text-white mb-4">
                  {step.title}
                </h3>
                <p className="text-white/45 text-sm font-light leading-relaxed font-['Outfit'] mb-6">
                  {step.desc}
                </p>
                <div className="border-t border-[#C9A84C]/10 pt-5">
                  <p className="text-[#C9A84C]/50 text-xs tracking-wider font-light font-['Outfit'] uppercase">
                    {step.detail}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/20 to-transparent" />
    </section>
  );
}
